import { type Coordinate, type PlacedSign, type UnitStatus, signById } from '@orgsim/shared';
import { useCallback, useEffect, useState } from 'react';
import { DrawTools } from '../map/DrawTools';
import { DrawingLayer } from '../map/DrawingLayer';
import { MapView } from '../map/MapView';
import { PatientMarker } from '../map/PatientMarker';
import { SignMarker } from '../map/SignMarker';
import { placementOrganisation } from '../signs/SignPalette';
import { useAppStore } from '../store/appStore';
import { useRunStore } from '../store/runStore';
import { type SyncState, useSyncStatus } from '../store/sync';
import { AlertCard } from './AlertCard';
import { ContextMenu } from './ContextMenu';
import { Icon } from './Icon';
import { MapDock } from './MapDock';
import { UNIT_STATUS_LABELS } from './labels';
import { GuidelinesPanel } from './panels/GuidelinesPanel';
import { InjectsPanel } from './panels/InjectsPanel';
import { PatientsPanel } from './panels/PatientsPanel';
import { ProtocolPanel } from './panels/ProtocolPanel';
import { SignDetails } from './panels/SignDetails';
import { UnitsPanel } from './panels/UnitsPanel';

type Tab = 'units' | 'patients' | 'injects' | 'protocol' | 'guidelines';

const SYNC_LABELS: Record<SyncState, string> = {
  synced: 'Server: gesichert',
  pending: 'Server: wird gesichert …',
  failed: 'Server: nicht gesichert',
  unauthenticated: 'Server: nicht angemeldet',
  forbidden: 'Server: gehört jemand anderem, nicht gesichert',
};

export function RunScreen() {
  const { run, state, dispatch, undo, finish, close, persistError } = useRunStore();
  const goStart = useAppStore((s) => s.goStart);
  const syncState = useSyncStatus((s) => s.state);
  const [tab, setTab] = useState<Tab>(() =>
    run?.finishedAt ? 'protocol' : run?.scenario.mode === 'lna' ? 'patients' : 'units',
  );
  const [selectedSign, setSelectedSign] = useState<string | null>(null);
  const [placingUnit, setPlacingUnit] = useState<string | null>(null); // unit id waiting for a map click
  const [menu, setMenu] = useState<{ at: { x: number; y: number }; sign: PlacedSign } | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [focusPatient, setFocusPatient] = useState<{ id: string } | null>(null);
  const [focusUnit, setFocusUnit] = useState<string | null>(null);
  const [mapFocus, setMapFocus] = useState<{ position: Coordinate } | null>(null);
  const [panelVisible, setPanelVisible] = useState(Boolean(run?.finishedAt));
  const [drawSession, setDrawSession] = useState(0);
  // The dispatch pops up only when the run is fresh: resuming a run that already has
  // events must not re-open something the candidate already saw.
  const [showAlert, setShowAlert] = useState(
    () => Boolean(run?.scenario.alert) && run?.events.length === 0,
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedSign(null);
        setPlacingUnit(null);
        setPanelVisible(false);
        setFocusPatient(null);
        setFocusUnit(null);
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'z' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  const onMapClick = useCallback(
    (position: Coordinate) => {
      // Leaflet fires every click listener registered on the map, so DrawTools cannot
      // suppress this one on its own; the drawing flag gates it here instead.
      if (drawing) return;
      if (placingUnit && state) {
        const unit = state.units[placingUnit];
        if (unit)
          dispatch({
            type: 'SignPlaced',
            signId: unit.id,
            signType: unit.type,
            position,
            label: unit.callsign,
          });
        setPlacingUnit(null);
        return;
      }
      if (selectedSign) {
        const def = signById(selectedSign);
        dispatch({
          type: 'SignPlaced',
          signId: crypto.randomUUID(),
          signType: selectedSign,
          position,
          label: def.label,
          organisation: placementOrganisation(selectedSign),
        });
        setSelectedSign(null);
        return;
      }
      setPanelVisible(false);
      setFocusPatient(null);
      setFocusUnit(null);
    },
    [drawing, placingUnit, selectedSign, state, dispatch],
  );

  if (!run || !state) return null;
  const readOnly = Boolean(run.finishedAt);
  const units = { id: 'units' as Tab, label: 'Kräfte' };
  const patients = { id: 'patients' as Tab, label: 'Patienten' };
  const tabs: { id: Tab; label: string }[] = [
    ...(run.scenario.mode === 'lna' ? [patients, units] : [units, patients]),
    { id: 'injects', label: 'Einspieler' },
    { id: 'protocol', label: 'Protokoll' },
    { id: 'guidelines', label: 'Vorgaben' },
  ];

  const openMapWindow = () => {
    window.open(
      `${location.pathname}?window=map&run=${run.id}`,
      'orgsim-map',
      'width=1280,height=800',
    );
  };

  const backToStart = () => {
    if (
      syncState !== 'synced' &&
      !window.confirm('Das Protokoll ist noch nicht auf dem Server gesichert. Trotzdem zurück?')
    )
      return;
    close();
    goStart();
  };

  const focusPatientOnMap = (patientId: string) => {
    chooseSign(null);
    setTab('patients');
    setFocusPatient({ id: patientId });
    setFocusUnit(null);
    setPanelVisible(true);
  };

  const cancelPlacement = () => {
    setSelectedSign(null);
    setPlacingUnit(null);
  };
  const chooseSign = (sign: string | null) => {
    setSelectedSign(sign);
    setPlacingUnit(null);
    setDrawing(false);
    setDrawSession((s) => s + 1);
    if (sign) setPanelVisible(false);
  };
  const placeUnit = (unitId: string | null) => {
    setPlacingUnit(unitId);
    setSelectedSign(null);
    setDrawing(false);
    setDrawSession((s) => s + 1);
    if (unitId) setPanelVisible(false);
  };
  const locateUnit = (unitId: string) => {
    const sign = state.signs[unitId];
    if (sign) setMapFocus({ position: sign.position });
    setFocusUnit(unitId);
    setFocusPatient(null);
  };

  const closeDetails = () => {
    setPanelVisible(false);
    setFocusPatient(null);
    setFocusUnit(null);
  };
  const openArea = (next: Tab) => {
    setPanelVisible(!(panelVisible && tab === next && !focusPatient && !focusUnit));
    setTab(next);
    setFocusPatient(null);
    setFocusUnit(null);
    cancelPlacement();
    setDrawing(false);
    setDrawSession((s) => s + 1);
  };
  const currentSign = focusUnit ? state.signs[focusUnit] : undefined;

  const confirmFinish = () => {
    if (window.confirm('Durchführung abschließen? Danach sind keine Änderungen mehr möglich.'))
      finish();
  };

  const menuItems = (sign: PlacedSign) => {
    const unit = state.units[sign.id];
    const items = unit
      ? (Object.keys(UNIT_STATUS_LABELS) as UnitStatus[]).map((s) => ({
          label: UNIT_STATUS_LABELS[s],
          onClick: () => dispatch({ type: 'UnitStatusChanged', unitId: unit.id, status: s }),
        }))
      : [];
    const rename = {
      label: 'Beschriftung ändern',
      onClick: () => {
        // Re-dispatching SignPlaced with the same signId overwrites the sign's entry
        // (reducer keys on signId), so this renames rather than creating a duplicate.
        const label = window.prompt('Beschriftung', sign.label ?? '');
        if (label !== null)
          dispatch({
            type: 'SignPlaced',
            signId: sign.id,
            signType: sign.signType,
            position: sign.position,
            label,
            organisation: sign.organisation,
          });
      },
    };
    return [
      ...items,
      rename,
      {
        label: 'Entfernen',
        danger: true,
        onClick: () => dispatch({ type: 'SignRemoved', signId: sign.id }),
      },
    ];
  };

  return (
    <div className="workspace simulation-workspace">
      <header className="workspace-header simulation-header">
        <div className="workspace-back">
          <button type="button" onClick={backToStart} className="button quiet">
            <Icon name="back" />
            Szenarien
          </button>
        </div>
        <div className="workspace-title">
          <strong>{run.scenario.name}</strong>
          <span>
            {readOnly ? 'Abgeschlossene Durchführung' : 'Durchführung'} ·{' '}
            {run.scenario.situation.keyword}
          </span>
        </div>
        <div className="button-group">
          <output
            className={`save-status ${syncState === 'synced' || syncState === 'pending' ? '' : 'warning'}`}
          >
            {SYNC_LABELS[syncState]}
          </output>
          {run.scenario.alert ? (
            <button type="button" onClick={() => setShowAlert(true)} className="button">
              <Icon name="bolt" size={16} />
              Alarmierung
            </button>
          ) : null}
          <button
            type="button"
            onClick={undo}
            disabled={readOnly || run.events.length === 0}
            className="button"
            title="Letzte Aktion rückgängig machen (Strg+Z)"
          >
            <Icon name="undo" size={16} />
            Rückgängig
          </button>
          <button
            type="button"
            onClick={openMapWindow}
            className="button"
            title="Karte ohne Ausbilderhinweise in einem eigenen Fenster öffnen"
          >
            <Icon name="external" size={16} />
            Kartenfenster
          </button>
          {!readOnly ? (
            <button type="button" onClick={confirmFinish} className="button">
              Abschließen
            </button>
          ) : null}
        </div>
      </header>
      {persistError ? (
        <div className="notice warning" role="alert">
          {persistError}
        </div>
      ) : null}
      <div className="work-area simulation-area">
        <div className="map-workspace">
          <div className="map-toolbar">
            <Icon name="map" size={16} />
            <span className="muted">Lagekarte</span>
            <span className="muted">
              · {Object.keys(state.patients).length} Patienten · {Object.keys(state.units).length}{' '}
              Kräfte
            </span>
            <button
              type="button"
              className="button quiet toolbar-end"
              aria-label="Zum Einsatzort"
              title="Zum Einsatzort"
              onClick={() => setMapFocus({ position: run.scenario.situation.location })}
            >
              <Icon name="target" size={17} />
            </button>
          </div>
          <div className={`map-canvas ${placingUnit || selectedSign ? 'is-placing' : ''}`}>
            <MapView
              center={run.scenario.situation.location}
              zoom={5}
              onClick={onMapClick}
              location={run.scenario.situation.location}
              focus={mapFocus}
            >
              {Object.values(state.signs).map((s) => (
                <SignMarker
                  key={s.id}
                  sign={s}
                  draggable={!readOnly}
                  onMove={(position) => dispatch({ type: 'SignMoved', signId: s.id, position })}
                  onContextMenu={(sign, at) => setMenu({ sign, at })}
                  selected={panelVisible && focusUnit === s.id}
                  onClick={() => {
                    chooseSign(null);
                    setTab('units');
                    setFocusUnit(s.id);
                    setFocusPatient(null);
                    setPanelVisible(true);
                  }}
                />
              ))}
              {Object.values(state.patients).map((p) => (
                <PatientMarker
                  key={p.id}
                  patient={p}
                  selected={panelVisible && focusPatient?.id === p.id}
                  onClick={() => focusPatientOnMap(p.id)}
                />
              ))}
              <DrawingLayer
                drawings={Object.values(state.drawings)}
                onContextMenu={(id) =>
                  !readOnly && dispatch({ type: 'DrawingRemoved', drawingId: id })
                }
              />
              {!readOnly ? (
                <DrawTools
                  key={drawSession}
                  onDrawing={(d) => dispatch({ type: 'DrawingAdded', drawing: d })}
                  drawingActive={setDrawing}
                  onActivate={() => {
                    cancelPlacement();
                    closeDetails();
                  }}
                />
              ) : null}
            </MapView>
            {!readOnly && (placingUnit || selectedSign) ? (
              <output className="placement-banner">
                <Icon name="pin" size={16} />
                <span>
                  {placingUnit
                    ? state.units[placingUnit]?.callsign
                    : signById(selectedSign as string).label}
                  : Position auf der Karte wählen.
                </span>
                <button type="button" className="button" onClick={cancelPlacement}>
                  Abbrechen <kbd>Esc</kbd>
                </button>
              </output>
            ) : null}
          </div>
        </div>
        <MapDock
          active={panelVisible ? tab : null}
          onSelect={openArea}
          items={tabs.map((t) => ({
            ...t,
            icon:
              t.id === 'units'
                ? 'truck'
                : t.id === 'patients'
                  ? 'people'
                  : t.id === 'injects'
                    ? 'bolt'
                    : t.id === 'guidelines'
                      ? 'book'
                      : 'history',
            count:
              t.id === 'units'
                ? Object.keys(state.units).length
                : t.id === 'patients'
                  ? Object.keys(state.patients).length
                  : t.id === 'injects'
                    ? run.scenario.injects.length - state.triggeredInjects.length
                    : t.id === 'protocol'
                      ? run.events.length
                      : undefined,
          }))}
        />
        {!panelVisible && !selectedSign && !placingUnit && !drawing ? (
          <div className="map-interaction-hint">
            <Icon name="cursor" size={15} />
            Patient oder Fahrzeug anklicken
          </div>
        ) : null}
        {panelVisible ? (
          <aside className="inspector context-inspector" aria-label="Objektdetails">
            <div className="drawer-heading">
              <h2>
                {currentSign
                  ? 'Zeichen auf der Karte'
                  : focusPatient
                    ? 'Patient sichten'
                    : tabs.find((t) => t.id === tab)?.label}
              </h2>
              <button
                type="button"
                className="button quiet icon-button"
                aria-label="Details schließen"
                onClick={closeDetails}
              >
                <Icon name="close" />
              </button>
            </div>
            {focusPatient || currentSign ? (
              <button
                type="button"
                className="all-objects"
                onClick={() => {
                  setFocusPatient(null);
                  setFocusUnit(null);
                }}
              >
                <Icon name="back" size={14} />
                {focusPatient ? 'Alle Patienten' : 'Alle Kräfte'}
              </button>
            ) : null}
            <div className="inspector-content">
              {tab === 'units' && currentSign ? (
                <SignDetails
                  key={currentSign.id}
                  sign={currentSign}
                  unit={state.units[currentSign.id]}
                  readOnly={readOnly}
                  dispatch={dispatch}
                  onRemove={closeDetails}
                />
              ) : null}
              {tab === 'units' && !currentSign ? (
                <UnitsPanel
                  state={state}
                  readOnly={readOnly}
                  selectedSign={selectedSign}
                  onSelectSign={chooseSign}
                  onPlaceUnit={placeUnit}
                  onLocate={locateUnit}
                  focusId={focusUnit}
                  dispatch={dispatch}
                />
              ) : null}
              {tab === 'patients' ? (
                <PatientsPanel
                  state={state}
                  readOnly={readOnly}
                  focusRequest={focusPatient}
                  onlyFocused={Boolean(focusPatient)}
                  onLocate={(id) => {
                    const p = state.patients[id];
                    if (p) {
                      setMapFocus({ position: p.position });
                      setFocusPatient({ id });
                    }
                  }}
                  limitedResources={run.scenario.limitedResources ?? false}
                  dispatch={dispatch}
                />
              ) : null}
              {tab === 'injects' ? (
                <InjectsPanel run={run} state={state} readOnly={readOnly} dispatch={dispatch} />
              ) : null}
              {tab === 'protocol' ? (
                <ProtocolPanel run={run} readOnly={readOnly} dispatch={dispatch} />
              ) : null}
              {tab === 'guidelines' ? <GuidelinesPanel /> : null}
            </div>
          </aside>
        ) : null}
      </div>
      <footer className="workspace-footer">
        <span>
          {run.events.length} Ereignisse ·{' '}
          {readOnly
            ? 'Abgeschlossen'
            : persistError
              ? 'Nicht gespeichert'
              : 'Änderungen werden lokal gespeichert'}
        </span>
        <span>Objekt anklicken für Details · Rechtsklick für Aktionen · Strg+Z rückgängig</span>
      </footer>
      {menu && !readOnly ? (
        <ContextMenu at={menu.at} items={menuItems(menu.sign)} onClose={() => setMenu(null)} />
      ) : null}
      {showAlert && run.scenario.alert ? (
        <AlertCard
          alert={run.scenario.alert}
          keyword={run.scenario.situation.keyword}
          onClose={() => setShowAlert(false)}
        />
      ) : null}
    </div>
  );
}
